import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { getUsageHistory } from "@/server/api/get-usage-history";
import { historyUsageMock } from "@/mocks/historyUsageMock";
import { useHistoryPeriod } from "@/composables/history/useHistoryPeriod";

type UsageTabKey = "internet" | "minutes" | "sms";

interface HistoryTab {
  key: UsageTabKey;
  title: string;
  active: boolean;
}

interface UsageHistoryItem {
  amount: number | string;
  balance: number;
  callingNumber: string;
  dialedNumber: string;
  duration: number;
  formattedDate: string;
  traffic: number;
  type: string;
  unit: string;
}

interface UsageOperation {
  amount: number;
  balance: number;
  callingNumber: string;
  dialedNumber: string;
  duration: number;
  time: string;
  traffic: number;
  type: string;
  unit: string;
}

interface UsageOperationGroup {
  date: string;
  operations: UsageOperation[];
}

const INTERNET_TYPE = "Интернет";
const SMS_TYPE = "SMS";
const VOICE_TYPES = ["Голосовая связь", "Голосовая связь международная"];

export function useHistoryOperations() {
  const { t } = useI18n();
  const {
    choosePeriod,
    formatDateToHuman,
    getPeriodStartEnd,
    isCustomPeriod,
    isOpenedSelect,
    monthNames,
    openSelect,
    periodOptions,
    periodText,
    resetPeriod,
    selectedPeriod,
  } = useHistoryPeriod();

  const activeTab = ref<UsageTabKey>("internet");
  const apiResponse = ref<UsageHistoryItem[]>([]);
  const tabTotals = ref<Record<UsageTabKey, number>>({
    internet: 0,
    minutes: 0,
    sms: 0,
  });

  const operationsTabs = computed<HistoryTab[]>(() => [
    {
      key: "internet",
      title: t("history.usage.tabs.internet"),
      active: activeTab.value === "internet",
    },
    {
      key: "minutes",
      title: t("history.usage.tabs.minutes"),
      active: activeTab.value === "minutes",
    },
    {
      key: "sms",
      title: t("history.usage.tabs.sms"),
      active: activeTab.value === "sms",
    },
  ]);

  function selectTab(tab: UsageTabKey) {
    activeTab.value = tab;
  }

  function isVoiceType(type: string): boolean {
    return VOICE_TYPES.includes(type);
  }

  function getOperationTab(type: string): UsageTabKey | null {
    if (type === INTERNET_TYPE) {
      return "internet";
    }

    if (isVoiceType(type)) {
      return "minutes";
    }

    if (type === SMS_TYPE) {
      return "sms";
    }

    return null;
  }

  function parseFormattedDate(value: string): {
    date: Date;
    dateISO: string;
    time: string;
  } {
    const [datePart = "", timePart = ""] = value.split(" г. ");
    const [day = "", monthName = "", year = ""] = datePart.split(" ");
    const monthIndex = monthNames.value.indexOf(monthName) + 1;
    const dateISO = `${year}-${String(monthIndex).padStart(2, "0")}-${String(
      day
    ).padStart(2, "0")}`;

    return {
      dateISO,
      time: timePart,
      date: new Date(`${dateISO}T${timePart}`),
    };
  }

  function calculateTabTotals() {
    tabTotals.value = {
      internet: 0,
      minutes: 0,
      sms: 0,
    };

    const { start } = getPeriodStartEnd();
    const startDate = new Date(start);

    for (const operation of apiResponse.value) {
      const parsedDate = parseFormattedDate(operation.formattedDate);

      if (parsedDate.date < startDate) {
        continue;
      }

      const tab = getOperationTab(operation.type);

      if (tab === "internet") {
        tabTotals.value.internet += (operation.traffic || 0) / 1024 / 1024;
      }

      if (tab === "minutes") {
        tabTotals.value.minutes += operation.duration
          ? Math.ceil(operation.duration / 60)
          : 0;
      }

      if (tab === "sms") {
        tabTotals.value.sms += 1;
      }
    }
  }

  const groupedOperations = computed<UsageOperationGroup[]>(() => {
    const groups: Record<string, UsageOperation[]> = {};
    const { start } = getPeriodStartEnd();
    const startDate = new Date(start);

    apiResponse.value.forEach((operation) => {
      const parsedDate = parseFormattedDate(operation.formattedDate);

      if (parsedDate.date < startDate) {
        return;
      }

      const normalized: UsageOperation = {
        time: parsedDate.time,
        type: operation.type,
        amount: Number(operation.amount),
        duration: operation.duration,
        callingNumber: operation.callingNumber,
        dialedNumber: operation.dialedNumber,
        traffic: operation.traffic,
        unit: operation.unit,
        balance: operation.balance,
      };

      if (!groups[parsedDate.dateISO]) {
        groups[parsedDate.dateISO] = [];
      }

      groups[parsedDate.dateISO].push(normalized);
    });

    return Object.entries(groups)
      .sort((left, right) => (left[0] < right[0] ? 1 : -1))
      .map(([date, operations]) => ({
        date: formatDateToHuman(date),
        operations: operations.sort((left, right) =>
          left.time < right.time ? 1 : -1
        ),
      }));
  });

  const filteredOperations = computed<UsageOperationGroup[]>(() =>
    groupedOperations.value
      .map((day) => ({
        ...day,
        operations: day.operations.filter(
          (operation) => getOperationTab(operation.type) === activeTab.value
        ),
      }))
      .filter((day) => day.operations.length > 0)
  );

  const totalSum = computed(() => {
    const total = tabTotals.value[activeTab.value];

    if (activeTab.value === "internet") {
      return Math.round(total * 100) / 100;
    }

    return total;
  });

  const totalUnit = computed(() => {
    if (activeTab.value === "internet") {
      return t("history.usage.units.gb");
    }

    if (activeTab.value === "minutes") {
      return t("history.usage.units.minutes");
    }

    return SMS_TYPE;
  });

  function getOperationTitle(operation: UsageOperation): string {
    if (isVoiceType(operation.type) || operation.type === SMS_TYPE) {
      return `+${operation.dialedNumber}`;
    }

    if (operation.type === INTERNET_TYPE) {
      return t("history.usage.trafficLabel");
    }

    return "";
  }

  function getOperationSubtitle(type: string): string {
    if (isVoiceType(type) || type === SMS_TYPE) {
      return t("history.usage.outgoing");
    }

    if (type === INTERNET_TYPE) {
      return t("history.usage.internet");
    }

    return "";
  }

  function getOperationValue(operation: UsageOperation): string {
    if (isVoiceType(operation.type)) {
      return operation.duration < 60
        ? `${operation.duration} ${t("history.usage.units.second")}`
        : `${Math.ceil(operation.duration / 60)} ${t(
            "history.usage.units.minute"
          )}`;
    }

    if (operation.type === SMS_TYPE) {
      return "1 SMS";
    }

    if (operation.type === INTERNET_TYPE) {
      const mb = operation.traffic / 1024;
      return `${mb < 1 ? mb.toFixed(2) : mb.toFixed(1)} ${t(
        "history.usage.units.mb"
      )}`;
    }

    return "";
  }

  function getOperationMeta(operation: UsageOperation): string {
    return `${operation.amount || 0} ₽ ${operation.time}`;
  }

  async function fetchOperations() {
    const { start, end } = getPeriodStartEnd();

    try {
      const response = await getUsageHistory(500, start, end);
      apiResponse.value = response.data.response ?? [];
    } catch (error) {
      console.log(error);
      apiResponse.value = historyUsageMock;
    } finally {
      calculateTabTotals();
    }
  }

  watch(selectedPeriod, fetchOperations);

  onMounted(() => {
    void fetchOperations();
  });

  return {
    activeTab,
    choosePeriod,
    filteredOperations,
    getOperationMeta,
    getOperationSubtitle,
    getOperationTitle,
    getOperationValue,
    isCustomPeriod,
    isOpenedSelect,
    openSelect,
    operationsTabs,
    periodOptions,
    periodText,
    resetPeriod,
    selectTab,
    selectedPeriod,
    totalSum,
    totalUnit,
  };
}
